import { type FunctionComponent } from "react";
import { BellOutlined, RobotOutlined } from "@ant-design/icons";
import { Badge, Button, Drawer, Empty, List, Switch, Tooltip } from "antd";

import ErrorBoundary from "~/components/ErrorBoundary";
import { useAppSelector } from "~/store/initializeStore";
import { AiRequestListItem } from "./AiRequestListItem";
import { useBackgroundTasks } from "./BackgroundTasksProvider";
import { useBackgroundTasksQuery } from "./useBackgroundTasks";
import { useTrainerWebPushNotifications } from "./useTrainerWebPushNotifications";

const BackgroundTasksDrawer: FunctionComponent = () => {
  const { isDrawerOpen, closeDrawer, toggleDrawer, showCompleted, setShowCompleted } = useBackgroundTasks();
  const { data, isLoading } = useBackgroundTasksQuery({ enabled: true, excludeCompleted: !showCompleted });
  const { canRequestPermission, requestPermission } = useTrainerWebPushNotifications();
  const currentUserId = useAppSelector((state) => state.user.data?.id);

  // The list is intentionally unfiltered for the account owner, so they keep
  // oversight of the whole team. The badge must not follow that: nagging the
  // owner to evaluate plans ordered by their trainers is noise. userId remains
  // the actor discriminator for both owner and team-member requests.
  //
  // taskCount is "things to evaluate" only for dish issues. A clip production carries the
  // number of candidates there, but the drawer entry is a single receipt — counting 10
  // candidates as 10 items made the badge say 11 over a list of two rows.
  const badgeCount = data
    ?.filter((task) => task.status === "TO_EVALUATE" && task.userId === currentUserId)
    .map((task) => (task.agentType === "studio_clips" ? 1 : task.taskCount || 1))
    .reduce((acc, curr) => acc + curr, 0);
  const hasRunningTasks = data?.some((task) => task.status === "SCHEDULED" || task.status === "STARTED") ?? false;

  return (
    <>
      <div
        role="button"
        className="relative inline-flex h-8 w-8 items-center justify-center"
        onClick={() => toggleDrawer()}
      >
        <Badge overflowCount={99} count={badgeCount ?? 0}>
          <RobotOutlined style={{ fontSize: 26 }} className="rotate-icon" />
        </Badge>
        {hasRunningTasks && (
          <Tooltip title="Zadanie w trakcie">
            <span className="absolute -bottom-0.5 -left-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white shadow-sm">
              <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
            </span>
          </Tooltip>
        )}
      </div>
      <Drawer
        title={
          <div className="flex items-center justify-between">
            <span>Zadania AI Agentów</span>
            <div className="flex items-center gap-2">
              {canRequestPermission && (
                <Tooltip title="Włącz powiadomienia przeglądarki">
                  <Button size="small" icon={<BellOutlined />} onClick={() => void requestPermission()} />
                </Tooltip>
              )}
              <span className="text-sm font-normal text-gray-500">Zakończone</span>
              <Switch size="small" checked={showCompleted} onChange={setShowCompleted} />
            </div>
          </div>
        }
        placement="right"
        onClose={closeDrawer}
        open={isDrawerOpen}
        width={600}
        className="bg-white"
      >
        <div className="flex flex-col gap-4">
          <List
            loading={isLoading}
            dataSource={data || []}
            locale={{
              emptyText: <Empty description="Brak zaplanowanych zadań" />,
            }}
            rowKey="id"
            renderItem={(task) => (
              <List.Item className="border-b border-gray-100 last:border-b-0">
                <ErrorBoundary>
                  <AiRequestListItem request={task} key={task.id} />
                </ErrorBoundary>
              </List.Item>
            )}
          />
        </div>
      </Drawer>
    </>
  );
};

export default BackgroundTasksDrawer;
